import { useCallback, useEffect, useState } from 'react';

import { searchIdentities, IdentityResponse } from '@api/v4/private/people_tagging/faces';
import useDebounce from '@components/common/custom_hooks/useDebounce';
import { Person } from '@components/people_tags/customPeopleTagTypes';

import { IdentitySearchReturnFormat, MOCK_IDENTITIES_RESPONSE } from './useIdentitySearch';

interface UseIdentitySearchOnDemandOptions {
  searchTerm: string;
  isDropdownOpen: boolean;
  limit?: number;
  excludeIdentityId?: string;
  returnFormat?: IdentitySearchReturnFormat;
  useMockData?: boolean;
}

export interface UseIdentitySearchOnDemandReturn<T = IdentityResponse | Person> {
  results: T[];
  isLoading: boolean;
  error: string | null;
}

/**
 * On-demand identity search hook that only fetches when the dropdown is open.
 * Unlike useIdentitySearch, this does NOT prefetch on mount — making it safe
 * to use in components rendered once per item (e.g. one per face in an asset).
 */
export const useIdentitySearchOnDemand = <T = IdentityResponse>({
  searchTerm,
  isDropdownOpen,
  limit = 50,
  excludeIdentityId,
  returnFormat = IdentitySearchReturnFormat.IDENTITY,
  useMockData = false
}: UseIdentitySearchOnDemandOptions): UseIdentitySearchOnDemandReturn<T> => {
  const [results, setResults] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearchTerm = useDebounce({
    value: searchTerm,
    delay: 600
  });

  const fetchResults = useCallback(async (term: string) => {
    setIsLoading(true);
    setError(null);

    try {
      let identities: IdentityResponse[];

      if (useMockData) {
        await new Promise((resolve) => setTimeout(resolve, 300));
        identities = MOCK_IDENTITIES_RESPONSE.filter((identity) => {
          if (!term.trim()) return true;
          return identity.name.toLowerCase().includes(term.toLowerCase());
        }).slice(0, limit);
      } else {
        const brandfolderKey = BFG.brandfolder_key;
        if (!brandfolderKey) {
          throw new Error('Brandfolder key not found');
        }

        identities = await searchIdentities({
          brandfolderKey,
          search: term || '',
          limit
        });
      }

      if (excludeIdentityId) {
        identities = identities.filter((identity) => identity.id !== excludeIdentityId);
      }

      let transformedResults: T[];

      if (returnFormat === IdentitySearchReturnFormat.PERSON) {
        transformedResults = identities.map((identity): Person => ({
          person_id: identity.id,
          tag_name: identity.name,
          face_uri: identity.identity_thumbnail_url || '',
          face_count: identity.face_count || 0
        })) as T[];
      } else {
        transformedResults = identities as T[];
      }

      setResults(transformedResults);
    } catch (err) {
      console.error('Error fetching identities:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch identities');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [limit, excludeIdentityId, returnFormat, useMockData]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }
    fetchResults(debouncedSearchTerm);
  }, [isDropdownOpen, debouncedSearchTerm, fetchResults]);

  return { results, isLoading, error };
};
